/**
 * Date formatting utilities for Garagr Vue applications
 */

/**
 * Format schedule date range for display
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @returns {string} Formatted date string like "Mon Jan 15 - Tue Jan 16"
 * @example
 * formatScheduleDate('2025-01-15', '2025-01-16')
 * // Returns: "Mon Jan 15 - Tue Jan 16"
 */
export function formatScheduleDate(startDate, endDate) {
  if (!startDate) return 'Date not set';
  
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;
  
  const startFormatted = start.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
  
  if (!end || start.toDateString() === end.toDateString()) {
    return startFormatted;
  }
  
  const endFormatted = end.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
  
  return `${startFormatted} - ${endFormatted}`;
}

/**
 * Format schedule date with custom options
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 * @example
 * formatScheduleDateWithOptions('2025-01-15', '2025-01-16', { year: 'numeric', month: 'long', day: 'numeric' })
 */
export function formatScheduleDateWithOptions(startDate, endDate, options = {}) {
  if (!startDate) return 'Date not set';
  
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : null;
  
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  };
  
  const formatOptions = { ...defaultOptions, ...options };
  const startFormatted = start.toLocaleDateString('en-US', formatOptions);
  
  if (!end || start.toDateString() === end.toDateString()) {
    return startFormatted;
  }
  
  const endFormatted = end.toLocaleDateString('en-US', formatOptions);
  return `${startFormatted} - ${endFormatted}`;
}

/**
 * Format a single date for display
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 * @example
 * formatDate('2025-01-15')
 * // Returns: "Jan 15, 2025"
 */
export function formatDate(date) {
  if (!date) return '';
  
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Get relative time string from timestamp
 * @param {string|Date|number} timestamp - Timestamp to convert
 * @returns {string} Relative time like "just now", "2 hours ago", "3 days ago"
 * @example
 * getRelativeTime(Date.now() - 3600000)
 * // Returns: "1 hour ago"
 */
export function getRelativeTime(timestamp) {
  if (!timestamp) return '';
  
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffSeconds = Math.floor((now - then) / 1000);
  
  if (diffSeconds < 60) {
    return 'just now';
  }
  
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  }
  
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  }
  
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  }
  
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
  }
  
  const diffYears = Math.floor(diffMonths / 12);
  return `${diffYears} year${diffYears !== 1 ? 's' : ''} ago`;
}
